import { getPlatforms } from "@ionic/react";

export const isEqual = <T>(obj1: T, obj2: T): boolean => {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  )
    return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;

    const property1 = obj1[key as keyof T];
    const property2 = obj2[key as keyof T];

    if (!isEqual(property1, property2)) return false;
  }
  return true;
};

export const extractPathFromUrl = (urlString: string): string | null => {
  try {
    // Guard against empty strings
    if (!urlString?.trim()) {
      return null;
    }

    // Ensure URL has protocol
    if (!urlString.startsWith("http://") && !urlString.startsWith("https://")) {
      urlString = `https://${urlString}`;
    }

    const url = new URL(urlString);
    // Guard against empty pathname
    return url.pathname === "/" ? null : url.pathname;
  } catch (error) {
    console.warn("Error parsing URL:", urlString, error);
    // Try a simpler path extraction if URL parsing fails
    try {
      const pathMatch = urlString.match(/\.app(.+)$/);
      if (pathMatch?.[1]) {
        return pathMatch[1];
      }
    } catch (e) {
      console.warn("Fallback path extraction failed:", e);
    }
    return null;
  }
};

export const LEGACY_FH_APP_LINKS = {
  default: "https://apps.apple.com/us/app/futurhealth/id6473443810",
  ios: "com.futurhealth.futurhealth://",
  android: "futurhealthapp",
};

export enum osEnum {
  android = "Android",
  ios = "iOS",
}

export const detectDevice = () => {
  const platforms = getPlatforms();

  if (platforms.includes("mobile")) {
    if (platforms.includes("android"))
      return { device: "mobile", os: osEnum.android };
    if (platforms.includes("ios")) return { device: "mobile", os: "iOS" };
    return { device: "mobile", os: osEnum.ios };
  }

  return { device: "desktop", os: "unknown" };
};

/**
 * Detects if the user is browsing from within an in-app browser
 * (Instagram, Facebook, TikTok, Twitter, LinkedIn, etc.)
 */
export const isInAppBrowser = (): boolean => {
  // Guard for non-browser environments (SSR)
  if (typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent || "";

  // Check for common in-app browser identifiers
  const inAppBrowserPatterns = [
    /Instagram/i,
    /FBAN/i, // Facebook App
    /FBAV/i, // Facebook App
    /FB_IAB/i, // Facebook In-App Browser
    /musical_ly/i, // TikTok (old name)
    /TikTok/i,
    /Twitter/i,
    /LinkedInApp/i,
    /Snapchat/i,
    /MicroMessenger/i, // WeChat
    /Line\//i,
  ];

  return inAppBrowserPatterns.some((pattern) => pattern.test(ua));
};

/**
 * Detects browser category from user agent for analytics
 * Distinguishes between in-app browsers and regular mobile browsers
 * @param userAgent - Optional user agent string. If not provided, will use navigator.userAgent when available
 */
export const getBrowserCategory = (userAgent?: string): string => {
  // Guard for browser environment - use provided UA or navigator.userAgent if available
  let ua: string;

  if (userAgent) {
    ua = userAgent.toLowerCase();
  } else if (typeof navigator !== "undefined" && navigator.userAgent) {
    ua = navigator.userAgent.toLowerCase();
  } else {
    return "Other";
  }

  if (
    ua.includes("musical_ly") ||
    ua.includes("bytelocale") ||
    ua.includes("tiktok")
  ) {
    return "TikTok In-App";
  }
  if (ua.includes("instagram")) {
    return "Instagram In-App";
  }
  if (ua.includes("fban") || ua.includes("fbav") || ua.includes("fb_iab")) {
    return "Facebook In-App";
  }
  if (ua.includes("android") && ua.includes("chrome")) {
    return "Chrome Android";
  }
  if (ua.includes("crios")) {
    return "Chrome iOS";
  }
  // Refine Mobile Safari detection to exclude desktop Safari
  if (
    ua.includes("safari") &&
    !ua.includes("chrome") &&
    (ua.includes("mobile") ||
      ua.includes("iphone") ||
      ua.includes("ipad") ||
      ua.includes("ipod"))
  ) {
    return "Mobile Safari";
  }
  return "Other";
};
